import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Badge, Modal, Form, Alert, Tab, Tabs, ProgressBar, ButtonGroup } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import fileService from '../services/fileService';

const FileManager = () => {
  const { currentUser: user } = useAuth();
  const [files, setFiles] = useState([]);
  const [myFiles, setMyFiles] = useState([]);
  const [adminFiles, setAdminFiles] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showAccessModal, setShowAccessModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedFileForAccess, setSelectedFileForAccess] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [storageInfo, setStorageInfo] = useState(null);
  const [uploadType, setUploadType] = useState('file'); // 'file' or 'folder'
  const [currentFolder, setCurrentFolder] = useState(null);
  const [folderPath, setFolderPath] = useState([]);
  const [formData, setFormData] = useState({
    accessLevel: 'PUBLIC',
    description: '',
    folderName: ''
  });
  const [accessFormData, setAccessFormData] = useState({
    userId: '',
    accessType: 'READ',
    expiresAt: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (user) {
      loadFiles();
      loadMyFiles();
      loadStorageInfo();
      if (isAdmin()) {
        loadAdminFiles();
        loadAllUsers();
      }
    } else {
      setLoading(false);
    }
  }, [user]);

  const isAdmin = () => {
    return user && user.roles && user.roles.includes('ROLE_ADMIN');
  };

  const loadFiles = async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      const response = await fileService.getFileList();
      setFiles(response.data.content);
      setLoading(false);
    } catch (error) {
      console.error('Error loading files:', error);
      setError('Failed to load files');
      setLoading(false);
    }
  };

  const loadMyFiles = async () => {
    try {
      const response = await fileService.getMyFiles();
      setMyFiles(response.data.content);
    } catch (error) {
      console.error('Error loading my files:', error);
    }
  };

  const loadAdminFiles = async () => {
    try {
      const response = await fileService.getAllFilesForAdmin();
      setAdminFiles(response.data.content);
    } catch (error) {
      console.error('Error loading admin files:', error);
    }
  };

  const loadAllUsers = async () => {
    try {
      const response = await fileService.getAllUsers();
      setAllUsers(response.data);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadStorageInfo = async () => {
    try {
      const response = await fileService.getStorageInfo();
      setStorageInfo(response.data);
    } catch (error) {
      console.error('Error loading storage info:', error);
    }
  };

  const handleFileSelect = (e) => {
    const files = e.target.files;

    if (uploadType === 'file') {
      setSelectedFile(files[0]);
      setSelectedFiles([]);
    } else if (uploadType === 'multiple' || uploadType === 'folderUpload') {
      setSelectedFiles(Array.from(files));
      setSelectedFile(null);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();

    if (uploadType === 'folder' && formData.folderName.trim() === '') {
      setError('Please enter a folder name');
      return;
    }

    if ((uploadType === 'file' && !selectedFile) ||
      ((uploadType === 'multiple' || uploadType === 'folderUpload') && selectedFiles.length === 0)) {
      setError('Please select file(s)');
      return;
    }

    try {
      setError('');
      setSuccess('');
      setUploadProgress(0);
      setIsUploading(true);

      console.log('Upload type:', uploadType);
      console.log('Selected file:', selectedFile);
      console.log('Selected files:', selectedFiles);
      console.log('Form data:', formData);

      if (uploadType === 'folder') {
        // Create folder
        await fileService.createFolder(
          formData.folderName,
          formData.accessLevel,
          formData.description,
          currentFolder
        );
        setSuccess('Folder created successfully!');
      } else if (uploadType === 'folderUpload' && selectedFiles.length > 0) {
        // Folder upload - upload multiple files with folder structure
        await fileService.uploadMultipleFiles(
          selectedFiles,
          formData.accessLevel,
          formData.description,
          currentFolder
        );
        setSuccess('Folder uploaded successfully!');
      } else if (selectedFiles.length > 0) {
        // Multiple file upload
        await fileService.uploadMultipleFiles(
          selectedFiles,
          formData.accessLevel,
          formData.description,
          currentFolder
        );
        setSuccess('Files uploaded successfully!');
      } else {
        // Single file upload with progress tracking
        await fileService.uploadFileWithFolder(
          selectedFile,
          formData.accessLevel,
          formData.description,
          currentFolder,
          null,
          (progress) => setUploadProgress(progress)
        );
        setSuccess('File uploaded successfully!');
      }

      setSelectedFile(null);
      setSelectedFiles([]);
      setFormData({ accessLevel: 'PUBLIC', description: '', folderName: '' });
      loadFiles();
      loadMyFiles();
      loadStorageInfo();
      if (isAdmin()) {
        loadAdminFiles();
      }

      setTimeout(() => {
        setShowUploadModal(false);
        setSuccess('');
        setIsUploading(false);
      }, 2000);
    } catch (error) {
      setIsUploading(false);
      console.error('Upload error:', error);
      console.error('Error response:', error.response);

      if (error.response?.data?.message) {
        setError(error.response.data.message);
      } else if (error.response?.data) {
        setError(`Upload failed: ${JSON.stringify(error.response.data)}`);
      } else {
        setError('Upload failed. Please try again.');
      }
    }
  };

  const handleDownload = async (storedName, originalName) => {
    try {
      const response = await fileService.downloadFile(storedName);

      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', originalName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      if (error.response?.status === 403) {
        setError('Access denied. Premium content requires premium membership.');
      } else {
        setError('Download failed. Please try again.');
      }
      console.error('Download error:', error);
    }
  };

  const handleDelete = async (fileId) => {
    if (window.confirm('Are you sure you want to delete this file?')) {
      try {
        await fileService.deleteFile(fileId);
        loadFiles();
        loadMyFiles();
      } catch (error) {
        setError('Failed to delete file');
        console.error('Delete error:', error);
      }
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const canAccessFile = (file) => {
    if (!file.premiumOnly) return true;
    return user && user.premium;
  };

  const handleGrantAccess = async (e) => {
    e.preventDefault();

    if (!accessFormData.userId) {
      setError('Please select a user');
      return;
    }

    try {
      setError('');
      await fileService.grantUserAccess(
        selectedFileForAccess.id,
        accessFormData.userId,
        accessFormData.accessType,
        accessFormData.expiresAt || null
      );

      setSuccess('Access granted successfully!');
      setAccessFormData({ userId: '', accessType: 'READ', expiresAt: '' });

      setTimeout(() => {
        setShowAccessModal(false);
        setSuccess('');
      }, 2000);
    } catch (error) {
      setError('Failed to grant access. Please try again.');
      console.error('Grant access error:', error);
    }
  };

  const handleRevokeAccess = async (fileId, userId) => {
    if (window.confirm('Are you sure you want to revoke access for this user?')) {
      try {
        await fileService.revokeUserAccess(fileId, userId);
        setSuccess('Access revoked successfully!');
        // Refresh the access list or files as needed
      } catch (error) {
        setError('Failed to revoke access');
        console.error('Revoke access error:', error);
      }
    }
  };

  const openAccessModal = (file) => {
    setSelectedFileForAccess(file);
    setShowAccessModal(true);
  };

  const getFileSizeLimit = () => {
    return isAdmin() ? '1TB' : '10MB';
  };

  const getAccessLevelBadge = (accessLevel) => {
    const variants = {
      'PUBLIC': 'success',
      'PREMIUM': 'warning',
      'PRIVATE': 'danger',
      'OWNER_ONLY': 'info'
    };
    return <Badge bg={variants[accessLevel] || 'secondary'}>{accessLevel}</Badge>;
  };

  if (loading) {
    return (
      <Container className="mt-5">
        <div className="text-center">
          <h2>Loading...</h2>
        </div>
      </Container>
    );
  }

  if (!user) {
    return (
      <Container className="mt-5">
        <Row>
          <Col>
            <h1 className="mb-4">File Manager</h1>
            <Alert variant="info">
              <h4>Authentication Required</h4>
              <p>You need to be logged in to access the File Manager.</p>
              <Button variant="primary" href="/login">
                Login
              </Button>
              <Button variant="outline-secondary" href="/register" className="ms-2">
                Register
              </Button>
            </Alert>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container className="mt-5">
      <Row>
        <Col>
          <h1 className="mb-4">File Manager</h1>
          {error && <Alert variant="danger">{error}</Alert>}
          {success && <Alert variant="success">{success}</Alert>}
        </Col>
      </Row>

      <Tabs defaultActiveKey="browse" className="mb-3">
        <Tab eventKey="browse" title="Browse Files">
          <Card>
            <Card.Body>
              <Table responsive striped hover>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Size</th>
                    <th>Type</th>
                    <th>Uploaded By</th>
                    <th>Access Level</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {files.map(file => (
                    <tr key={file.id}>
                      <td>
                        {file.isFolder && <i className="bi bi-folder-fill me-2"></i>}
                        {!file.isFolder && <i className="bi bi-file-earmark me-2"></i>}
                        {file.originalName}
                        {file.description && (
                          <small className="text-muted d-block">{file.description}</small>
                        )}
                      </td>
                      <td>{file.isFolder ? '-' : formatFileSize(file.size)}</td>
                      <td>{file.isFolder ? 'Folder' : file.contentType}</td>
                      <td>{file.uploadedBy}</td>
                      <td>{getAccessLevelBadge(file.accessLevel || (file.premiumOnly ? 'PREMIUM' : 'PUBLIC'))}</td>
                      <td>
                        {!file.isFolder && (
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => handleDownload(file.storedName, file.originalName)}
                            disabled={!canAccessFile(file)}
                          >
                            Download
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Tab>

        {user && (
          <Tab eventKey="my-files" title="My Files">
            <Row className="mb-3">
              <Col>
                <Button variant="primary" onClick={() => setShowUploadModal(true)}>
                  Upload Files/Folders
                </Button>
                {/* 
                  SHOW UPLOAD DIRECTORY BUTTON - COMMENTED OUT
                  To re-enable this button, uncomment the code block below in:
                  frontend/src/pages/FileManager.js (around line 425-440)
                */}
                {/*
                <Button 
                  variant="outline-info" 
                  size="sm" 
                  className="ms-2"
                  onClick={async () => {
                    try {
                      const response = await fileService.getUploadInfo();
                      alert(`Upload Directory Info:\n\nCurrent Directory: ${response.data.uploadDirAbsolute}\nExists: ${response.data.uploadDirExists}\nWritable: ${response.data.uploadDirWritable}`);
                    } catch (error) {
                      console.error('Error getting upload info:', error);
                    }
                  }}
                >
                  📁 Show Upload Directory
                </Button>
                */}
                <small className="text-muted ms-3">
                  File size limit: {getFileSizeLimit()}
                </small>
              </Col>
            </Row>

            {/* Storage Quota Display */}
            {storageInfo && (
              <Row className="mb-3">
                <Col>
                  <Card className="border-info">
                    <Card.Body className="py-2">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <strong>Storage Usage:</strong> {storageInfo.usedFormatted} / {storageInfo.quotaFormatted}
                          {!storageInfo.admin && (
                            <span className="text-muted ms-2">
                              ({storageInfo.usagePercentage.toFixed(1)}% used)
                            </span>
                          )}
                        </div>
                        {!storageInfo.admin && (
                          <div style={{ width: '200px' }}>
                            <ProgressBar
                              now={storageInfo.usagePercentage}
                              variant={storageInfo.usagePercentage > 80 ? 'danger' : storageInfo.usagePercentage > 60 ? 'warning' : 'success'}
                              size="sm"
                            />
                          </div>
                        )}
                        {storageInfo.admin && (
                          <Badge bg="success">Unlimited Storage</Badge>
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}

            <Row className="mb-3">
              <Col>
              </Col>
            </Row>

            <Card>
              <Card.Body>
                <Table responsive striped hover>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Size</th>
                      <th>Type</th>
                      <th>Access Level</th>
                      <th>Uploaded</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {myFiles.map(file => (
                      <tr key={file.id}>
                        <td>
                          {file.isFolder && <i className="bi bi-folder-fill me-2"></i>}
                          {!file.isFolder && <i className="bi bi-file-earmark me-2"></i>}
                          {file.originalName}
                          {file.description && (
                            <small className="text-muted d-block">{file.description}</small>
                          )}
                        </td>
                        <td>{file.isFolder ? '-' : formatFileSize(file.size)}</td>
                        <td>{file.isFolder ? 'Folder' : file.contentType}</td>
                        <td>{getAccessLevelBadge(file.accessLevel || (file.premiumOnly ? 'PREMIUM' : 'PUBLIC'))}</td>
                        <td>{new Date(file.createdAt).toLocaleDateString()}</td>
                        <td>
                          {!file.isFolder && (
                            <Button
                              size="sm"
                              variant="outline-primary"
                              className="me-1"
                              onClick={() => handleDownload(file.storedName, file.originalName)}
                            >
                              Download
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => file.isFolder ? handleDelete(file.id) : handleDelete(file.id)}
                          >
                            Delete
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Tab>
        )}

        {isAdmin() && (
          <Tab eventKey="admin" title="Admin - All Files">
            <Card>
              <Card.Body>
                <Table responsive striped hover>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Size</th>
                      <th>Type</th>
                      <th>Owner</th>
                      <th>Access Level</th>
                      <th>Uploaded</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {adminFiles.map(file => (
                      <tr key={file.id}>
                        <td>
                          {file.isFolder && <i className="bi bi-folder-fill me-2"></i>}
                          {!file.isFolder && <i className="bi bi-file-earmark me-2"></i>}
                          {file.originalName}
                          {file.description && (
                            <small className="text-muted d-block">{file.description}</small>
                          )}
                        </td>
                        <td>{file.isFolder ? '-' : formatFileSize(file.size)}</td>
                        <td>{file.isFolder ? 'Folder' : file.contentType}</td>
                        <td>{file.uploadedBy}</td>
                        <td>{getAccessLevelBadge(file.accessLevel)}</td>
                        <td>{new Date(file.createdAt).toLocaleDateString()}</td>
                        <td>
                          <ButtonGroup size="sm">
                            {!file.isFolder && (
                              <Button
                                variant="outline-primary"
                                onClick={() => handleDownload(file.storedName, file.originalName)}
                              >
                                Download
                              </Button>
                            )}
                            <Button
                              variant="outline-info"
                              onClick={() => openAccessModal(file)}
                            >
                              Manage Access
                            </Button>
                            <Button
                              variant="outline-danger"
                              onClick={() => handleDelete(file.id)}
                            >
                              Delete
                            </Button>
                          </ButtonGroup>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Tab>
        )}
      </Tabs>

      {/* Upload Modal */}
      <Modal show={showUploadModal} onHide={() => setShowUploadModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Upload Files/Folders</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleUpload}>
            {/* Upload Type Selection */}
            <Form.Group className="mb-3">
              <Form.Label>Upload Type</Form.Label>
              <Form.Select
                value={uploadType}
                onChange={(e) => {
                  console.log('Upload type changed to:', e.target.value);
                  setUploadType(e.target.value);
                  // Reset selections when type changes
                  setSelectedFile(null);
                  setSelectedFiles([]);
                }}
              >
                <option value="file">Single File</option>
                <option value="multiple">Multiple Files</option>
                <option value="folderUpload">Upload Folder</option>
                <option value="folder">Create Empty Folder</option>
              </Form.Select>
            </Form.Group>

            {/* File Selection */}
            {uploadType === 'file' && (
              <Form.Group className="mb-3">
                <Form.Label>
                  Select File
                  <small className="text-muted ms-2">(Max size: {getFileSizeLimit()})</small>
                </Form.Label>
                <Form.Control
                  type="file"
                  onChange={handleFileSelect}
                  required
                />
              </Form.Group>
            )}

            {uploadType === 'multiple' && (
              <Form.Group className="mb-3">
                <Form.Label>
                  Select Files
                  <small className="text-muted ms-2">(Max size per file: {getFileSizeLimit()})</small>
                </Form.Label>
                <Form.Control
                  type="file"
                  onChange={handleFileSelect}
                  multiple
                  required
                />
                <Form.Text className="text-muted">
                  You can select multiple files at once. Hold Ctrl/Cmd to select multiple files.
                </Form.Text>
              </Form.Group>
            )}

            {uploadType === 'folderUpload' && (
              <Form.Group className="mb-3">
                <Form.Label>
                  Select Folder
                  <small className="text-muted ms-2">(Max size per file: {getFileSizeLimit()})</small>
                </Form.Label>
                <input
                  type="file"
                  className="form-control"
                  onChange={handleFileSelect}
                  webkitdirectory=""
                  multiple
                  required
                />
                <Form.Text className="text-muted">
                  This will upload all files and subfolders from the selected folder.
                </Form.Text>
              </Form.Group>
            )}

            {/* Folder Name */}
            {uploadType === 'folder' && (
              <Form.Group className="mb-3">
                <Form.Label>Folder Name</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.folderName}
                  onChange={(e) => setFormData({ ...formData, folderName: e.target.value })}
                  placeholder="Enter folder name"
                  required
                />
              </Form.Group>
            )}

            {/* Access Level */}
            <Form.Group className="mb-3">
              <Form.Label>Access Level</Form.Label>
              <Form.Select
                value={formData.accessLevel}
                onChange={(e) => setFormData({ ...formData, accessLevel: e.target.value })}
              >
                <option value="PUBLIC">Public - Everyone can access</option>
                <option value="PREMIUM">Premium - Premium users only</option>
                <option value="PRIVATE">Private - Specific users only</option>
                <option value="OWNER_ONLY">Owner Only - Only you can access</option>
              </Form.Select>
            </Form.Group>

            {/* Description */}
            <Form.Group className="mb-3">
              <Form.Label>Description (Optional)</Form.Label>
              <Form.Control
                type="text"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description"
              />
            </Form.Group>

            {/* File Size Info */}
            {(selectedFile || selectedFiles.length > 0) && (
              <Alert variant="info">
                {uploadType === 'multiple' && selectedFiles.length > 0 && (
                  <div>Selected {selectedFiles.length} files</div>
                )}
                {uploadType === 'folderUpload' && selectedFiles.length > 0 && (
                  <div>Selected folder with {selectedFiles.length} files</div>
                )}
                {selectedFile && (
                  <div>Selected: {selectedFile.name} ({formatFileSize(selectedFile.size)})</div>
                )}
              </Alert>
            )}

            {uploadProgress > 0 && (
              <ProgressBar now={uploadProgress} label={`${uploadProgress}%`} className="mb-3" />
            )}

            <div className="d-flex justify-content-end">
              <Button variant="secondary" className="me-2" onClick={() => setShowUploadModal(false)}>
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                {uploadType === 'folder' ? 'Create Folder' : 'Upload'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Access Management Modal */}
      <Modal show={showAccessModal} onHide={() => setShowAccessModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Manage File Access</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedFileForAccess && (
            <>
              <h6>File: {selectedFileForAccess.originalName}</h6>
              <p className="text-muted">Current Access Level: {selectedFileForAccess.accessLevel}</p>

              <Form onSubmit={handleGrantAccess}>
                <Form.Group className="mb-3">
                  <Form.Label>Grant Access to User</Form.Label>
                  <Form.Select
                    value={accessFormData.userId}
                    onChange={(e) => setAccessFormData({ ...accessFormData, userId: e.target.value })}
                    required
                  >
                    <option value="">Select a user...</option>
                    {allUsers.map(user => (
                      <option key={user.id} value={user.id}>
                        {user.username} ({user.email})
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Access Type</Form.Label>
                  <Form.Select
                    value={accessFormData.accessType}
                    onChange={(e) => setAccessFormData({ ...accessFormData, accessType: e.target.value })}
                  >
                    <option value="READ">Read Only</option>
                    <option value="WRITE">Read & Write</option>
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Expires At (Optional)</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    value={accessFormData.expiresAt}
                    onChange={(e) => setAccessFormData({ ...accessFormData, expiresAt: e.target.value })}
                  />
                  <Form.Text className="text-muted">
                    Leave empty for permanent access
                  </Form.Text>
                </Form.Group>

                <div className="d-flex justify-content-end">
                  <Button variant="secondary" className="me-2" onClick={() => setShowAccessModal(false)}>
                    Cancel
                  </Button>
                  <Button variant="primary" type="submit">
                    Grant Access
                  </Button>
                </div>
              </Form>
            </>
          )}
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default FileManager;